import React from 'react';
import { ClaimState, OpportunityData, ClaimRecordData, ViewerData } from './types';
import { StatusPill } from './StatusPill';
import { CountdownChip } from './CountdownChip';
import { Lock, ExternalLink, History, Info } from 'lucide-react';
interface ClaimBannerProps {
  state: ClaimState;
  opportunity: OpportunityData;
  claim?: ClaimRecordData;
  viewer: ViewerData;
  slaHours: number;
  compact?: boolean;
}
export function ClaimBanner({
  state,
  opportunity,
  claim,
  viewer,
  slaHours,
  compact = false
}: ClaimBannerProps) {
  // Helper to format dates
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  // Determine UI configuration based on state
  let statusText = '';
  let statusType: 'neutral' | 'blue' | 'amber' | 'red' | 'green' | 'gray' = 'neutral';
  let primaryAction: {
    label: string;
    action: string;
    disabled: boolean;
    tooltip?: string;
  } | null = null;
  let secondaryActions: {
    label: string;
    action: string;
  }[] = [];
  let showMetadata = false;
  let urgency: 'healthy' | 'warning' | 'critical' = 'healthy';
  let message = '';
  switch (state) {
    case 'eligible':
      statusText = 'Available to Claim';
      statusType = 'neutral';
      primaryAction = {
        label: 'Claim Loan',
        action: 'claim',
        disabled: false
      };
      message = 'This opportunity is eligible for claiming from the 10+ Day List.';
      break;
    case 'not-eligible':
      statusText = 'Not Claimable';
      statusType = 'gray';
      message = 'This opportunity does not meet the criteria for claiming.';
      break;
    case 'in-progress-healthy':
      statusText = 'In Progress - Loan Claimed';
      statusType = 'blue';
      showMetadata = true;
      primaryAction = {
        label: 'View Claim',
        action: 'view',
        disabled: false
      };
      secondaryActions = [{
        label: 'Open Workspace',
        action: 'workspace'
      }];
      break;
    case 'in-progress-nearing-timeout':
      statusText = 'In Progress - Loan Claimed';
      statusType = 'amber';
      showMetadata = true;
      urgency = 'critical';
      primaryAction = {
        label: 'Take Over Loan',
        action: 'takeover',
        disabled: false
      };
      secondaryActions = [{
        label: 'View Claim',
        action: 'view'
      }];
      break;
    case 'in-progress-taken-over':
      statusText = 'In Progress - Taken Over';
      statusType = 'blue';
      showMetadata = true;
      primaryAction = {
        label: 'View Claim',
        action: 'view',
        disabled: false
      };
      secondaryActions = [{
        label: 'Open Workspace',
        action: 'workspace'
      }];
      break;
    case 'successful':
      statusText = 'Successful Claim';
      statusType = 'green';
      showMetadata = true;
      primaryAction = {
        label: 'View Claim History',
        action: 'history',
        disabled: false
      };
      break;
    case 'returned':
      statusText = 'Unsuccessful - Returned to List';
      statusType = 'red';
      showMetadata = true;
      primaryAction = {
        label: 'Claim Loan',
        action: 'claim',
        disabled: false
      };
      secondaryActions = [{
        label: 'View Claim History',
        action: 'history'
      }];
      message = 'Claim expired and returned to list. Available for new claim.';
      break;
    case 'retained':
      statusText = 'Unsuccessful - Retained by Owner';
      statusType = 'gray';
      showMetadata = true;
      primaryAction = {
        label: 'View Claim History',
        action: 'history',
        disabled: false
      };
      break;
    case 'permission-limited':
      statusText = 'In Progress - Loan Claimed';
      statusType = 'blue';
      showMetadata = true;
      primaryAction = {
        label: 'Take Over Loan',
        action: 'takeover',
        disabled: true,
        tooltip: 'You lack audience access to take over this claim.'
      };
      secondaryActions = [{
        label: 'View Claim',
        action: 'view'
      }];
      break;
  }
  return <div className={`w-full bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden flex items-stretch transition-all ${compact ? 'flex-col' : 'flex-col sm:flex-row'}`} data-id="element-31">
      {/* Left: Primary Status Block */}
      <div className={`p-4 flex-shrink-0 flex flex-col justify-center border-slate-100 ${compact ? 'border-b' : 'border-b sm:border-b-0 sm:border-r sm:w-64'} ${statusType === 'amber' || statusType === 'red' ? 'bg-red-50/30' : 'bg-slate-50/50'}`} data-id="element-32">
        <div className="flex flex-col gap-2" data-id="element-33">
          <div className="flex items-center gap-2 flex-wrap" data-id="element-34">
            <StatusPill status={statusText} type={statusType} data-id="element-35" />
            {slaHours && ['in-progress-healthy', 'in-progress-nearing-timeout', 'in-progress-taken-over', 'permission-limited'].includes(state) && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-200/50 px-1.5 py-0.5 rounded" data-id="element-36">
                  {slaHours}h SLA
                </span>}
          </div>
          {claim?.claim_Type__c && <div className="text-sm font-medium text-slate-900" data-id="element-37">
              {claim.claim_Type__c}
            </div>}
          {message && !showMetadata && <div className="text-xs text-slate-500 mt-1" data-id="element-38">{message}</div>}
        </div>
      </div>

      {/* Middle: Metadata Strip */}
      <div className="p-4 flex-grow flex flex-col justify-center min-w-0" data-id="element-39">
        {showMetadata && claim ? <div className={`grid gap-4 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`} data-id="element-40">
            <div className="flex flex-col gap-1" data-id="element-41">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-42">
                Claimed By
              </span>
              <div className="flex items-center gap-2" data-id="element-43">
                <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold" data-id="element-44">
                  {claim.team_Member__c.name.charAt(0)}
                </div>
                <span className="text-sm font-medium text-slate-900 truncate" data-id="element-45">
                  {claim.team_Member__c.name}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1" data-id="element-46">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-47">
                Loan Stage
              </span>
              <span className="text-sm font-medium text-slate-900 truncate" data-id="element-48">
                {opportunity.stageName}
              </span>
            </div>

            <div className="flex flex-col gap-1" data-id="element-49">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-50">
                Started At
              </span>
              <span className="text-sm text-slate-700" data-id="element-51">
                {formatDate(claim.claim_Date__c)}
              </span>
            </div>

            {['in-progress-healthy', 'in-progress-nearing-timeout', 'in-progress-taken-over', 'permission-limited'].includes(state) && <div className="flex flex-col gap-1" data-id="element-52">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-53">
                  Time Remaining
                </span>
                <div className="flex items-center gap-2" data-id="element-54">
                  <CountdownChip targetDate={claim.nextCutoffDate} urgency={urgency} data-id="element-55" />
                </div>
              </div>}

            {['successful', 'returned', 'retained'].includes(state) && message && <div className="flex flex-col gap-1 col-span-full mt-1" data-id="element-56">
                  <span className="text-sm text-slate-600 flex items-center gap-1.5" data-id="element-57">
                    <Info className="w-4 h-4 text-slate-400" data-id="element-58" />
                    {message}
                  </span>
                </div>}
          </div> : <div className="flex items-center h-full text-sm text-slate-500" data-id="element-59">
            {/* Empty state for metadata area when not applicable */}
            {state === 'eligible' && <div className="flex items-center gap-4" data-id="element-60">
                <div className="flex flex-col gap-1" data-id="element-61">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-62">
                    Loan Stage
                  </span>
                  <span className="text-sm font-medium text-slate-900 truncate" data-id="element-63">
                    {opportunity.stageName}
                  </span>
                </div>
              </div>}
          </div>}
      </div>

      {/* Right: Action Group */}
      <div className={`p-4 flex-shrink-0 flex flex-col justify-center gap-2 border-slate-100 bg-slate-50/30 ${compact ? 'border-t' : 'border-t sm:border-t-0 sm:border-l sm:w-64'}`} data-id="element-64">
        {primaryAction && <div className="relative group" data-id="element-65">
            <button disabled={primaryAction.disabled} className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors
                ${primaryAction.disabled ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : primaryAction.action === 'claim' || primaryAction.action === 'takeover' ? 'bg-brand-blue text-white hover:bg-blue-700 shadow-sm' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm'}
              `} style={!primaryAction.disabled && (primaryAction.action === 'claim' || primaryAction.action === 'takeover') ? {
          backgroundColor: '#0176D3'
        } : {}} data-id="element-66">
              {primaryAction.disabled && <Lock className="w-4 h-4" data-id="element-67" />}
              {primaryAction.label}
            </button>
            {primaryAction.disabled && primaryAction.tooltip && <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center" data-id="element-68">
                {primaryAction.tooltip}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-69"></div>
              </div>}
          </div>}

        {secondaryActions.length > 0 && <div className="flex flex-col gap-1.5 mt-1" data-id="element-70">
            {secondaryActions.map((action, idx) => <button key={idx} className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors" data-id="element-71">
                {action.action === 'workspace' ? <ExternalLink className="w-3.5 h-3.5" data-id="element-72" /> : <History className="w-3.5 h-3.5" data-id="element-73" />}
                {action.label}
              </button>)}
          </div>}
      </div>
    </div>;
}